import {Component, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {ModalService} from '~shared/components/elements/global-modal/modal.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {PaginatedTableComponent} from '~shared/components/tables/paginated-table/paginated-table.component';
import {TablesTranslations} from '~shared/components/tables/tables-translations.service';
import {SidebarService} from '~core/components/sidebar/sidebar.service';
import {ActionButtonType} from '~core/enum';
import {ActionButton, ActionButtonEvent, Attribute, Club, Lottery, SidebarOptions, TixxSelectSubmission} from '~core/models';
import {TixxSelectSubmissionStatusTranslationsService, TooltipTranslationService} from '~shared/translations';
import {
  AttributeService,
  NavigationService,
  ScreenSizeService,
  StorageService,
  TixxSelectService,
  TixxSelectSubmissionService
} from '~core/services';

/**
 * Component that displays the lottery preparation process
 */
@UntilDestroy()
@Component({
  selector: 'app-lottery-preparation',
  templateUrl: './lottery-preparation.component.html'
})

export class LotteryPreparationComponent implements OnInit {
  @ViewChild(PaginatedTableComponent) paginatedTable: PaginatedTableComponent;

  public selectedLottery: Lottery;
  public isOpened: boolean = true;
  public loading: boolean = false;
  public currentSubmissions: Array<TixxSelectSubmission>;
  public multiselectData: Array<any> = [];
  public lotteryPreparationForm: UntypedFormGroup;
  public clubAttributes: Array<Attribute>;
  public priorityDropdown: Array<any> = [];

  public i18nDeleteTitle: string;
  public i18nDeleteParagraph: string;
  public i18nEmptyState: string;

  public baseApi: string;
  public customQueries: Array<any> = [];
  public columns: Array<string> = [];
  public keys: Array<Array<string>> = [];
  public actionButtons: Array<ActionButton> = [];

  constructor(
    private storage: StorageService,
    private navigationService: NavigationService,
    private sidebarService: SidebarService,
    private tixxSelectService: TixxSelectService,
    protected attributeService: AttributeService,
    private modalService: ModalService,
    private formBuilder: UntypedFormBuilder,
    private tablesTranslations: TablesTranslations,
    private submissionService: TixxSelectSubmissionService,
    public buttonsTranslations: ButtonsTranslations,
    public flTranslations: FormLabelTranslationService,
    private tooltipTranslations: TooltipTranslationService,
    private statusTranslations: TixxSelectSubmissionStatusTranslationsService,
    private screenSizeService: ScreenSizeService,

  ) {
    if (this.screenSizeService.screenSize === 'desktop') {
      this.sidebarService.setSidebar(new SidebarOptions(true, true));
    } else {
      this.sidebarService.setSidebar(new SidebarOptions(false, true));
    }
    this.sidebarService.castIsOpened.pipe(untilDestroyed(this))
      .subscribe(sidebarOptions => this.isOpened = sidebarOptions.isOpened);
    this.navigationService.setCurrentPage('lotteryPreparation');
    this.navigationService.setCurrentBreadcrumbs(['crm', 'lotteryPreparation']);
    this.submissionService.castSubmissionList.pipe(untilDestroyed(this))
      .subscribe((data: Array<TixxSelectSubmission>) => {
        if (data) {
          this.currentSubmissions = data;
        }
      });
    this.attributeService.castClubAttributes.pipe(untilDestroyed(this))
      .subscribe((data: Array<Attribute>) => {
        if (data) {
          this.clubAttributes = data;
          this.fillSelectData();
        }
      });
    this.storage.castSelectedClub.pipe(untilDestroyed(this)).subscribe((data: Club) => {
      if (!this.clubAttributes) {
        this.loading = true;
        this.attributeService.getClubAttribute(data.id).pipe(untilDestroyed(this)).subscribe((res: any) => {
          this.attributeService.announceClubAttributes(res);
          this.loading = false;
        });
      }
    });
    this.tixxSelectService.castSelectedLottery.pipe(untilDestroyed(this))
      .subscribe((lottery: Lottery) => this.selectedLottery = lottery);

    this.i18nDeleteTitle = $localize`:@@lotteryPreparation.text.noSubmissions:There are no submissions`;

  }

  ngOnInit(): void {
    this.submissionService.setSubmissionsList([]);
    this.getLatestSubmissions(this.selectedLottery.id);
    this.setUp();
    this.fillLotteryForm();
  }

  /**
   * Sets the new reminder list
   */
  public createSubmissionObject(reminders: Array<TixxSelectSubmission>): void {
    const data = this.submissionService.createSubmissionObject(reminders);
    this.submissionService.setSubmissionsList(data);
  }

  /**
   * Create the lottery form.
   */
  public fillLotteryForm(): void {
    this.lotteryPreparationForm = this.formBuilder.group({
      'customer.firstName': [''],
      'customer.lastName': [''],
      'customer.postalCode': [''],
      'customer.street': [''],
      'customer.attributes[]': [''],
      'orderRequest.bankAccount.iban': [''],
      'customer.email': [''],
      'priority.name': [''],
    });
  }

  public formSubmit(form: UntypedFormGroup): void {
    this.customQueries.length = 0;
    for (const prop in form.value) {
      if (!form.value[prop] || !form.value[prop]?.length) {
        // eslint-disable-next-line
        delete form.value[prop];
      }
    }

    this.customQueries.push(form.value);
    this.paginatedTable.refetchData();
  }

  /**
   * Checks which action button was clicked
   */
  public actionButtonClicked(actionButton: ActionButtonEvent<TixxSelectSubmission>): void {
    const priority = {
      priority: {
        name: actionButton.type
      }
    };
    switch (actionButton.type) {
      case ActionButtonType.DELETE:
        this.showDeleteModal(actionButton.data.id);
        break;
      default:
        this.submissionService.editSubmissionPriority(actionButton.data.id, priority)
          .subscribe((submission: TixxSelectSubmission) => {
            this.submissionService.updateSubmission(submission);
          });
    }
  }

  /**
   * Adds the columns and keys for the table
   */
  protected setUp(): void {
    this.columns.push(
      this.tablesTranslations.i18nDateOfSubmission,
      this.tablesTranslations.i18nFirstName,
      this.tablesTranslations.i18nLastName,
      this.tablesTranslations.i18nStreet,
      this.tablesTranslations.i18nPostalCode,
      this.tablesTranslations.i18nPriority
    );
    this.keys.push(['submittedAt', 'customer.firstName', 'customer.lastName', 'customer.street', 'customer.postalCode', 'priority']);
    this.actionButtons.push(
      new ActionButton({
        type: 'prioritized',
        icon: '#esf-icon-circle-outlined-check',
        tooltip: this.tooltipTranslations.i18nPrioritize,
        disabled: { key: 'priority', value: 'prioritized' }
      }),
      new ActionButton({
        type: 'deferred',
        icon: '#esf-icon-circle-outlined-close',
        tooltip: this.tooltipTranslations.i18nDefer,
        disabled: { key: 'priority', value: 'deferred' }
      }),
      new ActionButton({
        type: 'normal',
        icon: '#esf-icon-reset',
        tooltip: this.tooltipTranslations.i18nReset,
        disabled: { key: 'priority', value: 'normal' }
      }),
      new ActionButton({
        type: ActionButtonType.DELETE,
        icon: '#esf-icon-trash',
        tooltip: this.tooltipTranslations.i18nDelete,
        disabled: { key: 'publicLottery.status.name', value: val => val === 'execution' || val === 'completed' }
      })
    );
    this.i18nDeleteTitle = $localize`:@@tixxSelect.modalTitle.deleteSubmission:Delete Submission?`;
    this.i18nDeleteParagraph = $localize`:@@tixxSelect.modalParagraph.deleteSubmission:When deleting this submission the information will be lost and the submission will be removed. Would you like to delete the submission?`;
    this.priorityDropdown.push(
      { value: '', status: this.statusTranslations.i18nAny },
      { value: 'normal', status: this.statusTranslations.i18nNormal },
      { value: 'prioritized', status: this.statusTranslations.i18nPrioritized },
      { value: 'deferred', status: this.statusTranslations.i18nDeferred },
    );
  }

  /**
   * Get the reminders for the current customer.
   */
  public getLatestSubmissions(id: number): any {
    const endpoint = this.submissionService.getLotterySubmissionEndpoint(id);
    this.baseApi = endpoint[0];
    this.customQueries = endpoint[1];
  }

  /**
   * Fill the data for select form field.
   */
  public fillSelectData(): void {
    const data = this.clubAttributes;

    for (const att of data) {
      const obj = { id: att.id, name: att.name };
      this.multiselectData.push(obj);
    }
  }

  public async showDeleteModal(id: string): Promise<any> {
    this.modalService.setModalOptions(this.i18nDeleteTitle, this.i18nDeleteParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      this.submissionService.delete(id).subscribe(() => {
        this.submissionService.deleteSubmissionsList(id);
        this.modalService.setStatus(false);
      });
    }
  }
}
